import {
  REVENUE_STATUS_LABEL,
  VAI_TRO_CHIA_LABEL,
  laKenhKiemTien,
  type ChiaNhanSuKy,
  type KenhChiaNhanSu,
  type RevenueOverview,
} from '@qlk/shared';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { useKyChung } from '../../../layout/kyChung.js';
import { coQuyen, useDuongDanTrang, usePhamViDuLieu, useToi } from '../../../lib/auth.js';
import {
  Avatar,
  DataTable,
  EmptyState,
  KhoiChuaCoKenh,
  KhoiMuc,
  Money,
  Nut,
  PageHeader,
  StatStrip,
  StatusBadge,
  type CotBang,
  type OChiSo,
} from '../../../ui/index.js';
import { tenKy } from '../../../lib/ky.js';
import {
  duongDanXuatExcel,
  useChiaNhanSu,
  useDoanhThuKenh,
  useTongQuanDoanhThu,
  useTongQuanKenh,
} from '../api.js';
import { BieuDoThang } from '../components/BieuDoThang.js';
import { KhoiDongTien } from '../components/KhoiDongTien.js';
import { KhungAdsense } from '../components/KhungAdsense.js';

type DongDoiTac = RevenueOverview['byPartner'][number];

export function TrangDoanhThu() {
  const duongDan = useDuongDanTrang('TÀI CHÍNH');
  const toi = useToi();
  const coSua = coQuyen(toi.data?.permissions, 'revenue', 'edit');

  /*
   * Kỳ lấy từ ô chọn **chung** ở thanh trên (`?ky=`), kiểu `thang-don`: API
   * doanh thu chỉ nhận `YYYY-MM`, không gộp quý được, nên ô chung của trang này
   * cố ý không bày quý (xem `layout/kyChung`).
   */
  const ky = useKyChung();
  const period = ky.giaTri;

  /*
   * Hai tổng quan loại trừ nhau, chọn theo **phạm vi dữ liệu của vai trò**:
   *
   * - toàn công ty → `/overview`: dòng tiền công ty, có Content ID kho nhạc,
   *   phí kho nhạc và quy đổi VND;
   * - chỉ kênh được giao → `/tong-quan-kenh`: cùng chuỗi trừ, chỉ cộng các kênh
   *   được xem, không có ba khoản trên.
   *
   * `/overview` trả **403** cho người bị giới hạn kênh (rà soát bảo mật
   * 16/10/2026, mục V4a) và vẫn sẽ như thế. Trước bản vá này trang không có
   * nhánh nào cho họ, nên vai trò "Quản lý kênh" mở trang là thấy đúng một
   * khung đỏ "Không tải được dữ liệu doanh thu" — lỗi gặp trên bản chạy thật
   * 19/10/2026.
   *
   * Chờ `daBiet` mới gọi: đoán sai một nhịp là nháy đúng khung đỏ đó.
   */
  const { biGioiHanKenh, daBiet } = usePhamViDuLieu();
  const tongQuan = useTongQuanDoanhThu(period, 6, daBiet && !biGioiHanKenh);
  const tongQuanKenh = useTongQuanKenh(period, 6, daBiet && biGioiHanKenh);

  /** Một khuôn cho cả hai nhánh, để phần thân trang bên dưới chỉ viết một lần. */
  const nguon = biGioiHanKenh
    ? {
        dongTien: tongQuanKenh.data?.cashflow,
        monthly: (tongQuanKenh.data?.monthly ?? []).map((m) => ({
          period: m.period,
          channelNet: m.channelNet,
          musicNet: '0.00',
          total: m.channelNet,
        })),
        adsenseChannels: tongQuanKenh.data?.adsenseChannels ?? [],
        byPartner: tongQuanKenh.data?.byPartner ?? [],
        dangTai: !daBiet || tongQuanKenh.isPending,
        loi: tongQuanKenh.error,
        taiLai: () => void tongQuanKenh.refetch(),
      }
    : {
        dongTien: tongQuan.data?.cashflow,
        monthly: tongQuan.data?.monthly ?? [],
        adsenseChannels: tongQuan.data?.adsenseChannels ?? [],
        byPartner: tongQuan.data?.byPartner ?? [],
        dangTai: !daBiet || tongQuan.isPending,
        loi: tongQuan.error,
        taiLai: () => void tongQuan.refetch(),
      };

  const theoKenh = useDoanhThuKenh(period);
  /*
   * Phần chia nhân sự của kỳ — ô thống kê, các cột chia của bảng "Theo kênh" và
   * khối "Chia nhân sự theo người". **Một** lượt gọi cho cả ba, và máy chủ đã
   * che theo người xem (`xemDayDu`).
   */
  const chia = useChiaNhanSu(period);

  /*
   * Cùng một luật đếm với Tổng quan và tab Quản lý kênh (xem `laKenhKiemTien`):
   * phải **đang khai là kiếm tiền** và có doanh thu > 0 trong kỳ.
   *
   * Dòng doanh thu tháng nay có mang `monetizationStatus` (module 08 kéo từ
   * module 01), nên không còn phải suy từ mỗi doanh thu: kênh bị tắt kiếm tiền
   * giữa kỳ vẫn còn tiền của mấy ngày đầu, suy ra sẽ đếm nhầm là đang kiếm tiền.
   */
  const soKenhKiemTien = (theoKenh.data ?? []).filter((d) =>
    laKenhKiemTien({ monetizationStatus: d.monetizationStatus, grossRevenue: d.grossUsed }),
  ).length;

  /*
   * "theo hợp đồng" chỉ được ghi khi tỷ lệ **đúng là** tỷ lệ một hợp đồng. Kênh
   * vào Network giữa kỳ có tỷ lệ bình quân của nhiều đoạn (`tyLeBinhQuan`), đem
   * số đó đi tra hợp đồng thì không có hợp đồng nào khớp.
   */
  const moTaTyLe = useMemo(() => {
    const dong = (theoKenh.data ?? []).filter((d) => Number(d.sharePercent ?? 0) > 0);
    const tyLe = dong.map((d) => Number(d.sharePercent ?? 0));
    if (tyLe.length === 0) return 'Chưa có kênh nào qua Network';
    const binhQuan = dong.some((d) => d.tyLeBinhQuan);
    const nhoNhat = Math.min(...tyLe);
    const lonNhat = Math.max(...tyLe);
    if (nhoNhat !== lonNhat) return `${String(nhoNhat)}–${String(lonNhat)}% theo kênh`;
    return binhQuan ? `bình quân ${String(nhoNhat)}%` : `${String(nhoNhat)}% theo hợp đồng`;
  }, [theoKenh.data]);

  const cot: CotBang<DongDoiTac>[] = [
    {
      key: 'nguon',
      header: 'Nguồn',
      render: (p) =>
        p.partnerId ? (
          <Link to={`/network/${p.partnerId}`} className="font-semibold text-chu hover:text-nhan">
            {p.partnerName}
          </Link>
        ) : (
          <span className="font-semibold text-chu">{p.partnerName}</span>
        ),
    },
    {
      key: 'apDung',
      header: 'Áp dụng cho',
      render: (p) => (
        <span className="text-chu-phu">
          {p.channelCount} kênh{p.partnerId === '' ? ' (chưa gán đối tác)' : ''}
        </span>
      ),
    },
    {
      key: 'gop',
      header: `Gộp ${tenKy(period)}`,
      align: 'right',
      render: (p) => <Money value={p.gross} />,
    },
    {
      key: 'phanNw',
      header: 'Phần Network',
      align: 'right',
      render: (p) => <Money value={p.partnerShare} />,
    },
    {
      key: 'thucNhan',
      /*
       * `thucNhan` (sau VAT), **không** `net` (= công ty nhận, trước VAT).
       *
       * Cột này tên "Thực nhận" nhưng trước đây render `p.net`, trong khi dòng
       * "Tổng" của cùng bảng lấy `cashflow.channelNet` — một số **sau** VAT.
       * Nên cộng dọc cột không bao giờ ra dòng Tổng, và chênh đúng bằng VAT.
       * `byPartner[]` đã có sẵn trường `thucNhan` đúng nghĩa.
       *
       * Không đổi công thức nào: `thucNhan` của mỗi đối tác vẫn là tổng cột
       * `thucNhan` của chính các dòng doanh thu thuộc đối tác đó
       * (`layTheoDoiTac`), tức cùng một chuỗi `tinhThucNhan`.
       */
      header: 'Thực nhận',
      align: 'right',
      render: (p) => <Money value={p.thucNhan} className="font-semibold text-ok-chu" />,
    },
  ];

  const tongKet = nguon.dongTien
    ? {
        nguon: 'Tổng',
        gop: <Money value={nguon.dongTien.gross} />,
        phanNw: <Money value={nguon.dongTien.partnerShare} />,
        thucNhan: <Money value={nguon.dongTien.channelNet} />,
      }
    : undefined;

  const soKenhPhuTrach = tongQuanKenh.data?.soKenh ?? toi.data?.assignedChannelIds?.length ?? 0;

  return (
    <>
      <PageHeader
        breadcrumb={duongDan}
        title="Doanh thu"
        phu={
          biGioiHanKenh
            ? `Kỳ ${tenKy(period)} · Doanh thu các kênh bạn phụ trách (${String(soKenhPhuTrach)} kênh)`
            : `Kỳ ${tenKy(period)}`
        }
        giaiThich={
          biGioiHanKenh
            ? 'Chỉ số liệu của kênh bạn phụ trách, theo đúng thứ tự trừ: gộp → thuế Mỹ → phần Network → VAT → thực nhận. Content ID kho nhạc, phí kho nhạc và tỷ giá là khoản của cả công ty nên không hiện ở đây.'
            : 'Doanh thu kênh qua Network, AdSense trực tiếp và doanh thu Content ID từ nhạc Miss Records. Số của kỳ ưu tiên sao kê, sau đó API, sau đó nhập tay.'
        }
        actions={
          <>
            <Link
              to="/doanh-thu/doi-soat"
              className="vung-bam inline-flex items-center justify-center rounded-[10px] border border-vien bg-be-mat px-4 text-co-ban font-semibold text-chu hover:bg-neutral-nen"
            >
              Đối soát
            </Link>
            {/*
              "Nhập nhạc & tỷ giá" đi tới màn hình gồm Content ID kho nhạc, phí
              kho nhạc và tỷ giá — cả ba đều trả 403 cho người bị giới hạn kênh,
              nên nút chỉ dẫn tới một trang ba khung đỏ. Ẩn.
            */}
            {coSua && !biGioiHanKenh && (
              <Link
                to="/doanh-thu/nhap"
                className="vung-bam inline-flex items-center justify-center rounded-[10px] border border-vien bg-be-mat px-4 text-co-ban font-semibold text-chu hover:bg-neutral-nen"
              >
                Nhập nhạc & tỷ giá
              </Link>
            )}
            {/*
              File của người bị giới hạn kênh là **bản rút gọn**: chỉ sheet "Theo
              kênh", chỉ kênh được giao, không có cột VND (xem `excel.ts`). Đổi
              nhãn cho khớp — "cho kế toán" là báo cáo tài chính của cả công ty,
              mà bản rút gọn thì không phải.
            */}
            <a
              href={duongDanXuatExcel(period, !biGioiHanKenh)}
              className="vung-bam inline-flex items-center justify-center rounded-[10px] bg-nhan px-4 text-co-ban font-semibold text-white hover:bg-nhan-dam"
            >
              {biGioiHanKenh ? 'Xuất Excel kênh của bạn' : 'Xuất Excel cho kế toán'}
            </a>
          </>
        }
      />

      <KhoiChuaCoKenh tenTrang="Doanh thu" />

      {/*
        Dải chỉ số: chuỗi tiền của kỳ, đi tới tận cuối phép trừ. Người xem đủ
        có năm ô (tới "Công ty giữ lại"), người xem hẹp có bốn ô và ô cuối là
        phần của chính họ — máy chủ không gửi hai số kia.
      */}
      <div className="mb-5">
        <StatStrip
          aria-label={`Chia % nhân sự · kỳ ${tenKy(period)}`}
          o={oChiSoChia(nguon.dongTien, chia.data, biGioiHanKenh, tenKy(period))}
        />
      </div>

      {nguon.loi ? (
        <div className="rounded-the border border-vien bg-be-mat">
          <EmptyState
            loi
            title="Không tải được dữ liệu doanh thu"
            description={nguon.loi.message}
            action={<Nut onClick={nguon.taiLai}>Thử lại</Nut>}
          />
        </div>
      ) : (
        <>
          <KhoiDongTien
            dongTien={nguon.dongTien}
            soKenhKiemTien={soKenhKiemTien}
            moTaTyLe={moTaTyLe}
          />

          {/* Biểu đồ tháng + khung AdSense: hai cột khi vùng nội dung ≥ 1100px. */}
          <div className="mb-6 grid grid-cols-1 gap-4 @min-[1100px]/noi-dung:grid-cols-[1.6fr_1fr]">
            <BieuDoThang duLieu={nguon.monthly} dangTai={nguon.dangTai} chiKenh={biGioiHanKenh} />
            <KhungAdsense duLieu={nguon.adsenseChannels} dangTai={nguon.dangTai} coSua={coSua} />
          </div>

          <div className="mb-5">
            <KhoiMuc tieuDe="Nguồn thanh toán" satLe>
              <DataTable
                caption="Doanh thu theo từng Network và AdSense"
                columns={cot}
                rows={nguon.byPartner}
                keyOf={(p) => p.partnerId || 'khong-doi-tac'}
                dangTai={nguon.dangTai}
                {...(tongKet ? { tongKet } : {})}
                khiRong={<EmptyState title="Kỳ này chưa có doanh thu nào" />}
              />
            </KhoiMuc>
          </div>

          <BangTheoKenh period={period} chia={chia.data} />

          {/* Khối "Chia nhân sự theo người" — chỉ người xem đủ nhận `theoNguoi`. */}
          {chia.data?.theoNguoi && <KhoiChiaTheoNguoi chia={chia.data} />}
        </>
      )}
    </>
  );
}

/* --------------------------- Dải chỉ số chia -------------------------- */

/**
 * Ô thống kê của chuỗi chia, theo đúng thứ tự tiền đã chốt 14/10/2026.
 *
 * | Người xem đủ | Người xem hẹp |
 * |---|---|
 * | Gộp · Thực nhận · Lợi nhuận kênh · Chia nhân sự · Công ty giữ lại | Gộp kênh bạn quản lý · Thực nhận · Lợi nhuận kênh · Thu nhập của bạn |
 *
 * Gộp và thực nhận lấy từ khối **dòng tiền** đang hiện ngay dưới (cùng một
 * `cashflow`), ba ô sau lấy từ `chiaTheoKenh`. Hai đường cho một ô là hai số
 * lệch nhau, nên không ô nào đổi nguồn giữa hai bản.
 */
function oChiSoChia(
  dongTien: { gross: string; channelNet: string } | undefined,
  chia: ChiaNhanSuKy | undefined,
  biGioiHanKenh: boolean,
  nhanKy: string,
): OChiSo[] {
  const phuTamTinh =
    chia === undefined
      ? 'chưa có số của kỳ này'
      : chia.tamTinh
        ? 'tạm tính · chốt ở trang Đối soát nhân sự'
        : 'đã chốt ở trang Đối soát nhân sự';

  const o: OChiSo[] = [
    {
      nhan: `${biGioiHanKenh ? 'Gộp kênh bạn quản lý' : 'Doanh thu gộp'} · ${nhanKy}`,
      giaTri: <Money value={dongTien?.gross ?? null} />,
      phu: 'trước thuế Mỹ và phần Network',
    },
    {
      nhan: `Thực nhận · ${nhanKy}`,
      giaTri: <Money value={dongTien?.channelNet ?? null} />,
      phu: 'sau thuế Mỹ, Network và VAT',
    },
    {
      nhan: `Lợi nhuận kênh · ${nhanKy}`,
      giaTri: <Money value={chia?.tong.loiNhuan ?? null} />,
      phu: 'thực nhận trừ chi phí vận hành',
    },
  ];

  if (chia?.xemDayDu === true) {
    o.push(
      {
        nhan: `Chia nhân sự · ${nhanKy}`,
        giaTri: <Money value={chia.tong.chiaNhanSu ?? null} />,
        phu: `${String(chia.tong.soNguoi ?? 0)} người · ${phuTamTinh}`,
      },
      {
        nhan: `Công ty giữ lại · ${nhanKy}`,
        giaTri: <Money value={chia.tong.congTyGiuLai ?? null} />,
        phu: 'lợi nhuận kênh trừ phần chia nhân sự',
      },
    );
  } else {
    o.push({
      nhan: `Thu nhập của bạn · ${nhanKy}`,
      giaTri: <Money value={chia?.tong.phanCuaToi ?? null} />,
      phu: phuTamTinh,
    });
  }

  return o;
}

/* ----------------------- Chia nhân sự theo người ---------------------- */

type DongNguoi = NonNullable<ChiaNhanSuKy['theoNguoi']>[number];

/**
 * Khối "Chia nhân sự theo người" — chỉ người xem đủ.
 *
 * Tổng của khối này **khớp bản nháp đối soát** khi kỳ là trọn một tháng: cả hai
 * đi qua cùng `gopDoiSoat` trên cùng tập dòng (xem
 * `shared/chiaNhanSu.gopChiaNhanSu`).
 */
function KhoiChiaTheoNguoi({ chia }: { chia: ChiaNhanSuKy }) {
  const cot: CotBang<DongNguoi>[] = [
    {
      key: 'nguoi',
      header: 'Nhân sự',
      render: (n) => (
        <span className="flex items-center gap-2.5">
          <Avatar name={n.tenNguoi} color={n.avatarColor} size={28} />
          <span className="truncate font-semibold text-chu">{n.tenNguoi}</span>
        </span>
      ),
    },
    {
      key: 'vaiTro',
      header: 'Vai trò',
      render: (n) => n.vaiTro.map((v) => VAI_TRO_CHIA_LABEL[v]).join(', ') || '—',
    },
    {
      key: 'soKenh',
      header: 'Số kênh',
      align: 'right',
      render: (n) => <span className="so-lieu">{n.soKenh}</span>,
    },
    {
      key: 'phanChia',
      header: chia.tamTinh ? 'Tạm tính trong kỳ' : 'Trong kỳ',
      align: 'right',
      render: (n) => <Money value={n.phanChia} className="font-semibold text-chu" />,
    },
  ];

  return (
    <div className="mt-5">
      <KhoiMuc
        tieuDe="Chia nhân sự theo người"
        moTa={
          chia.tamTinh
            ? 'Số tạm tính của kỳ. Chốt và trả tiền ở trang Đối soát nhân sự.'
            : 'Kỳ đã chốt — số lấy từ bản đối soát đang hiệu lực.'
        }
      >
        <DataTable
          caption="Phần chia của từng nhân sự trong kỳ"
          columns={cot}
          rows={chia.theoNguoi ?? []}
          keyOf={(n) => n.userId}
          tongKet={{
            nguoi: 'Tổng',
            soKenh: <span className="so-lieu">{chia.tong.soKenh}</span>,
            phanChia: <Money value={chia.tong.chiaNhanSu ?? 0} />,
          }}
          khiRong={<EmptyState title="Kỳ này chưa ai trong đội ngũ được chia" />}
        />
      </KhoiMuc>
    </div>
  );
}

/* --------------------------- Bảng theo kênh --------------------------- */

function BangTheoKenh({ period, chia }: { period: string; chia: ChiaNhanSuKy | undefined }) {
  const theoKenh = useDoanhThuKenh(period);
  const chiaTheoKenh = new Map((chia?.theoKenh ?? []).map((k) => [k.channelId, k]));
  const xemDayDu = chia?.xemDayDu === true;

  const cot: CotBang<(typeof dong)[number]>[] = [
    {
      key: 'kenh',
      header: 'Kênh',
      render: (d) => (
        <Link to={`/kenh/${d.channelId}`} className="font-semibold text-chu hover:text-nhan">
          {d.channelName}
        </Link>
      ),
    },
    { key: 'network', header: 'Network', render: (d) => d.partnerName ?? '—' },
    {
      key: 'nguon',
      header: 'Nguồn số liệu',
      render: (d) => (
        <StatusBadge tone={d.grossSource === 'statement' ? 'info' : 'neutral'} cham={false}>
          {d.grossSource === 'statement'
            ? 'Sao kê Network'
            : d.grossSource === 'api'
              ? 'YouTube API'
              : 'Nhập tay'}
        </StatusBadge>
      ),
    },
    /*
     * Thứ tự cột = **thứ tự trừ** của `tinhThucNhan`: gộp → thuế Mỹ → sau thuế →
     * phần Network → công ty nhận → VAT → thực nhận. Trước đây cột "Phần NW"
     * đứng trước "Thuế Mỹ" và tính trên gộp, nên đọc dọc bảng không ra được số
     * thực nhận.
     */
    { key: 'gop', header: 'Gộp', align: 'right', render: (d) => <Money value={d.grossUsed} /> },
    {
      key: 'thueMy',
      header: 'Thuế Mỹ',
      align: 'right',
      render: (d) => <Money value={d.thueMy} />,
    },
    {
      key: 'sauThue',
      header: 'Sau thuế',
      align: 'right',
      render: (d) => <Money value={d.sauThue} />,
    },
    {
      key: 'phanNw',
      header: 'Phần NW',
      align: 'right',
      render: (d) => <Money value={d.partnerShare} />,
    },
    {
      key: 'congTyNhan',
      header: 'Công ty nhận',
      align: 'right',
      render: (d) => <Money value={d.net} />,
    },
    { key: 'vat', header: 'VAT', align: 'right', render: (d) => <Money value={d.vat} /> },
    {
      key: 'thucNhan',
      header: 'Thực nhận',
      align: 'right',
      render: (d) => <Money value={d.thucNhan} className="font-semibold text-ok-chu" />,
    },
    /*
     * Chuỗi tiền tiếp tục sau "Thực nhận". Người xem đủ thấy bốn cột tới "Công
     * ty giữ lại"; người xem hẹp thấy "Lợi nhuận kênh · Vai trò · % · Phần của
     * bạn" — máy chủ không gửi số của người khác.
     */
    ...(xemDayDu
      ? ([
          {
            key: 'chiPhi',
            header: 'Chi phí',
            align: 'right',
            render: (d) => <OChia giaTri={chiaTheoKenh.get(d.channelId)?.chiPhiVanHanh} />,
          },
          {
            key: 'loiNhuan',
            header: 'Lợi nhuận kênh',
            align: 'right',
            render: (d) => <OChia giaTri={chiaTheoKenh.get(d.channelId)?.loiNhuan} am />,
          },
          {
            key: 'chiaNhanSu',
            header: 'Chia nhân sự',
            align: 'right',
            /*
             * Kênh **chưa có dòng tỷ lệ nào** hiện `—`, không hiện `0,00 US$`.
             *
             * Từ 26/10/2026 bảng này gồm cả kênh chưa gán nhân sự (xem
             * `team.chiaTheoKenh`), mà `0` ở ô đó đọc ra là "có người được chia
             * nhưng ra không đồng" — một câu sai. Cùng luật với `phanCuaToi`.
             */
            render: (d) => <OChia giaTri={chiaCuaKenh(chiaTheoKenh.get(d.channelId))} />,
          },
          {
            key: 'congTyGiuLai',
            header: 'Công ty giữ lại',
            align: 'right',
            render: (d) => <OChia giaTri={chiaTheoKenh.get(d.channelId)?.congTyGiuLai} am />,
          },
        ] satisfies CotBang<(typeof dong)[number]>[])
      : ([
          {
            key: 'loiNhuan',
            header: 'Lợi nhuận kênh',
            align: 'right',
            render: (d) => <OChia giaTri={chiaTheoKenh.get(d.channelId)?.loiNhuan} am />,
          },
          {
            key: 'vaiTroPct',
            header: 'Vai trò · %',
            render: (d) => {
              const k = chiaTheoKenh.get(d.channelId);
              if (!k || k.vaiTroCuaToi.length === 0) {
                return <span className="text-chu-mo">—</span>;
              }
              return (
                <span className="text-chu-phu">
                  {k.vaiTroCuaToi.map((v) => VAI_TRO_CHIA_LABEL[v]).join(', ')}
                  {k.pctCuaToi !== null && (
                    <span className="so-lieu">
                      {' · '}
                      {k.pctCuaToi.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%
                    </span>
                  )}
                </span>
              );
            },
          },
          {
            key: 'phanCuaToi',
            header: 'Phần của bạn',
            align: 'right',
            render: (d) => <OChia giaTri={chiaTheoKenh.get(d.channelId)?.phanCuaToi} />,
          },
        ] satisfies CotBang<(typeof dong)[number]>[])),
    {
      key: 'lech',
      header: 'Lệch',
      align: 'right',
      render: (d) =>
        d.diffPercent === null ? (
          <span className="text-chu-mo">—</span>
        ) : (
          <span
            className={`so-lieu ${Math.abs(Number(d.diffPercent)) > 2 ? 'font-semibold text-bad-chu' : 'text-chu-phu'}`}
          >
            {Number(d.diffPercent) > 0 ? '+' : ''}
            {Number(d.diffPercent).toFixed(1).replace('.', ',')}%
          </span>
        ),
    },
    {
      key: 'trangThai',
      header: 'Trạng thái',
      render: (d) => (
        <StatusBadge
          tone={d.status === 'reconciled' ? 'ok' : d.status === 'final' ? 'info' : 'neutral'}
        >
          {REVENUE_STATUS_LABEL[d.status]}
        </StatusBadge>
      ),
    },
  ];

  const dong = theoKenh.data ?? [];
  /* Kênh có ngày trong kỳ không thuộc Network nào: thường do ngày vào Network sai. */
  const thieuNetwork = dong.filter((d) => d.thieuNetwork);
  /* Kênh có ngày chưa khai thuế Mỹ: những ngày đó đang tính mặc định 30%. */
  const thieuThueMy = dong.filter((d) => d.thieuThueMy);

  return (
    <KhoiMuc tieuDe="Doanh thu từng kênh">
      {thieuNetwork.length > 0 && (
        <p className="mb-4 rounded-[8px] bg-warn-nen px-3 py-2 text-co-ban text-warn-chu">
          Kỳ này chưa có Network — kiểm tra ngày vào Network:{' '}
          {thieuNetwork.map((d) => d.channelName).join(', ')}. Phần Network của những ngày đó đang
          tính 0%.
        </p>
      )}

      {thieuThueMy.length > 0 && (
        <p className="mb-4 rounded-[8px] bg-warn-nen px-3 py-2 text-co-ban text-warn-chu">
          Chưa khai thuế Mỹ cho cả kỳ: {thieuThueMy.map((d) => d.channelName).join(', ')}. Những
          ngày chưa có hợp đồng đang tính mặc định 30% — khai "Thuế Mỹ khấu trừ" ở hợp đồng Network
          hoặc cài đặt AdSense.
        </p>
      )}

      {theoKenh.error ? (
        <EmptyState
          loi
          title="Không tải được danh sách kênh"
          description={theoKenh.error.message}
        />
      ) : (
        <DataTable
          caption="Doanh thu từng kênh trong kỳ"
          columns={cot}
          rows={dong}
          keyOf={(d) => d.id}
          dangTai={theoKenh.isPending}
          {...(chia
            ? {
                /*
                 * Dòng tổng cộng **đúng con số máy chủ đã gộp qua cent**, không
                 * cộng lại từ các dòng đang hiện: cộng hai lần là hai kết quả,
                 * và bảng này còn có thể thiếu kênh không có doanh thu.
                 */
                tongKet: {
                  kenh: 'Tổng',
                  gop: <Money value={tongGop(dong)} />,
                  thucNhan: <Money value={chia.tong.thucNhan} />,
                  ...(xemDayDu
                    ? {
                        chiPhi: <Money value={chia.tong.chiPhiVanHanh} />,
                        loiNhuan: <Money value={chia.tong.loiNhuan} />,
                        chiaNhanSu: <Money value={chia.tong.chiaNhanSu ?? 0} />,
                        congTyGiuLai: <Money value={chia.tong.congTyGiuLai ?? 0} />,
                      }
                    : {
                        loiNhuan: <Money value={chia.tong.loiNhuan} />,
                        phanCuaToi: <Money value={chia.tong.phanCuaToi} />,
                      }),
                },
              }
            : {})}
          khiRong={<EmptyState title="Kỳ này chưa có kênh nào có doanh thu" />}
        />
      )}
    </KhoiMuc>
  );
}

/** Ô tiền của một cột chia; `undefined` = kênh không có dòng chia nào ⇒ `—`. */
/** Tổng chia của một kênh; `undefined` khi kênh chưa có dòng tỷ lệ nào ⇒ `—`. */
function chiaCuaKenh(k: KenhChiaNhanSu | undefined): number | undefined {
  if (!k || k.dong.length === 0) return undefined;
  return k.chiaNhanSu;
}

function OChia({ giaTri, am }: { giaTri: number | null | undefined; am?: boolean }) {
  if (giaTri === null || giaTri === undefined) return <span className="text-chu-mo">—</span>;
  return <Money value={giaTri} className={am === true && giaTri < 0 ? 'text-giam' : ''} />;
}

/** Tổng cột "Gộp" của các dòng đang hiện — cột này không có trong `chia.tong`. */
function tongGop(dong: { grossUsed: string }[]): number {
  return Math.round(dong.reduce((t, d) => t + Number(d.grossUsed) * 100, 0)) / 100;
}
